import type { CSSProperties } from 'react';
import { t } from './lib/lingua';

/**
 * Il marchio Molo, fermo e in movimento.
 *
 * Riprende il segno proposto in `web/design/screens.html` (schermata 06):
 * l'impalcatura di un molo — l'impiantito, tre pali, l'acqua che passa sotto —
 * e sopra l'anello d'ormeggio. "Molo" è il posto dove le navi ormeggiano, non
 * dove vengono costruite: il vocabolario giusto per un pannello di gestione.
 *
 * Le attese del pannello sono fatte con lo stesso segno, mai con un elemento
 * nuovo: si muovono solo impiantito, pali, acqua e anello, con durate e curve
 * dei token `--md-sys-motion-*`. Ognuna dice una cosa diversa, e ce n'è una
 * sola per contesto (studio del marchio, tavole «Animazioni» e «In uso»):
 *
 *   marea        solo l'acqua scorre: attesa di fondo che non blocca nulla
 *   ormeggio     l'anello pulsa e manda un cerchio: si sta stabilendo un contatto
 *   costruzione  il molo si monta e si smonta: l'avvio a freddo e il carico
 *                di una sezione
 *   livello      la marea sale e ridiscende: attesa con un fine ma senza percentuale
 *   anello       il solo anello che gira, dentro un bottone o una riga
 *
 * E una che non è un ciclo: la **costruzione determinata**, dove la quantità
 * di segno disegnata è la percentuale di passi conclusi e l'acqua arriva solo
 * a lavoro finito. L'animazione smette di essere decorazione e diventa il dato.
 *
 * Il logotipo è in Sora, la faccia del marchio, spedita col binario invece che
 * linkata da Google: senza rete un logo che ripiega sul font di sistema smette
 * di essere un logo. Non sta in `@meshble/molo` perché Sora serve a una parola
 * sola: il testo del pannello è in Manrope, i titoli in Outfit.
 *
 * Le facce si rigenerano con lo script in `package.json` (`npm run fonts`).
 */

/** I tre tratti del segno, nell'ordine in cui il molo si monta. */
const DECK = 32;
const PILES = 40.5;
const RING = 20.1;

export type Animazione = 'marea' | 'ormeggio' | 'costruzione' | 'livello' | 'anello';

export interface LogoProps {
  size?: number;
  /** Ferma se omessa. Una sola per contesto. */
  anim?: Animazione;
  /** `error` per un lavoro fallito: il segno resta com'è e cambia colore. */
  tone?: 'primary' | 'error' | 'inherit';
  className?: string;
  /** Per un'attesa che ha un significato: diventa `aria-label`, e il segno smette di essere decorativo. */
  label?: string;
}

const COLORE: Record<NonNullable<LogoProps['tone']>, string | undefined> = {
  primary: 'var(--md-sys-color-primary)',
  error: 'var(--md-sys-color-error)',
  inherit: undefined,
};

export function Logo({ size = 56, anim, tone = 'primary', className, label }: LogoProps) {
  const cls = ['molo-mark', anim && `molo-mark--${anim}`, className].filter(Boolean).join(' ');
  const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true as const };
  return (
    <svg viewBox="0 0 40 40" width={size} height={size} className={cls} style={{ color: COLORE[tone] }} {...a11y}>
      <path className="molo-mark__deck" d="M4 15.5 H36" />
      <path className="molo-mark__piles" d="M11 15.5 V29 M20 15.5 V29 M29 15.5 V29" />
      {anim === 'marea' ? (
        // L'acqua scorre: il tracciato è più lungo del segno e si sposta di un
        // periodo d'onda per volta dentro un ritaglio. Il clipPath ha un id
        // proprio per ogni istanza, o due marchi nella stessa pagina si
        // ritaglierebbero a vicenda.
        <>
          <defs><clipPath id={`molo-marea-${size}`}><rect x="4" y="28" width="32" height="10" /></clipPath></defs>
          <g clipPath={`url(#molo-marea-${size})`}>
            <path className="molo-mark__water" d="M-12 33.5 q4-3.2 8 0 t8 0 t8 0 t8 0 t8 0 t8 0 t8 0 t8 0" />
          </g>
        </>
      ) : (
        <path className="molo-mark__water" d="M4 33.5 q4-3.2 8 0 t8 0 t8 0 t8 0" />
      )}
      {anim === 'ormeggio' && <circle className="molo-mark__ripple" cx="20" cy="9" r="3.2" />}
      <circle className="molo-mark__ring" cx="20" cy="9" r="3.2" />
    </svg>
  );
}

export interface LogoAvanzamentoProps {
  step: number;
  total: number;
  size?: number;
  /** Il job è fallito: il molo resta dov'è e passa a `error`. */
  failed?: boolean;
  className?: string;
}

/**
 * La costruzione determinata: il molo cresce coi passi veri.
 *
 * Impiantito, pali e anello si spartiscono i 92,6 punti di tratto in ordine
 * di montaggio; l'acqua compare solo all'ultimo passo, ed è il segnale che il
 * lavoro è finito. La crescita di ogni passo è una transizione con la curva
 * standard di M3, non una durata inventata. Sotto, il segno intero in
 * filigrana dice quanto manca.
 */
export function LogoAvanzamento({ step, total, size = 40, failed, className }: LogoAvanzamentoProps) {
  const quota = total > 0 ? Math.max(0, Math.min(1, step / total)) : 0;
  const tratto = quota * (DECK + PILES + RING);
  const resto = (lunghezza: number, speso: number) =>
    Math.max(0, Math.min(lunghezza, lunghezza - (tratto - speso)));
  const finito = total > 0 && step >= total;
  const st = (lunghezza: number, speso: number): CSSProperties => ({
    strokeDasharray: lunghezza,
    strokeDashoffset: resto(lunghezza, speso).toFixed(2),
  });
  return (
    <svg
      viewBox="0 0 40 40"
      width={size}
      height={size}
      className={['molo-mark', 'molo-mark--cresce', className].filter(Boolean).join(' ')}
      style={{ color: failed ? 'var(--md-sys-color-error)' : 'var(--md-sys-color-primary)' }}
      role="img"
      aria-label={total > 0 ? t('Passo {n} di {totale}', { n: step, totale: total }) : t('In attesa')}
    >
      <g className="molo-mark__filigrana" aria-hidden="true">
        <path className="molo-mark__deck" d="M4 15.5 H36" />
        <path className="molo-mark__piles" d="M11 15.5 V29 M20 15.5 V29 M29 15.5 V29" />
        <circle className="molo-mark__ring" cx="20" cy="9" r="3.2" />
      </g>
      <path className="molo-mark__deck" d="M4 15.5 H36" style={st(DECK, 0)} />
      <path className="molo-mark__piles" d="M11 15.5 V29 M20 15.5 V29 M29 15.5 V29" style={st(PILES, DECK)} />
      <path className="molo-mark__water" d="M4 33.5 q4-3.2 8 0 t8 0 t8 0 t8 0" style={{ opacity: finito ? 0.5 : 0 }} />
      <circle className="molo-mark__ring" cx="20" cy="9" r="3.2" style={st(RING, DECK + PILES)} />
    </svg>
  );
}

/**
 * L'anello che gira, da solo: l'indicatore circolare per i posti stretti.
 * Sta dentro un bottone che sta lavorando, alla misura del testo.
 */
export function Anello({ size = 18, className }: { size?: number; className?: string }) {
  return (
    <svg viewBox="0 0 40 40" width={size} height={size} className={['molo-mark', 'molo-mark--anello', className].filter(Boolean).join(' ')} aria-hidden="true">
      <circle className="molo-mark__ring" cx="20" cy="9" r="3.2" style={{ strokeWidth: 3 }} />
    </svg>
  );
}

/**
 * Il carico di una sezione: il molo che si monta e si smonta, al centro dello
 * spazio sotto il titolo — la stessa attesa dell'avvio, e di quando l'hub non
 * risponde. Prima era la barra d'ormeggio, una riga di quattro pixel sotto il
 * titolo, che su una pagina ancora vuota non si notava.
 *
 * Nel flusso è alta zero, così quando i dati arrivano la pagina non salta: un
 * velo copre lo scheletro finché serve. Si passa `attiva` solo per il primo
 * carico: un aggiornamento con i dati già a schermo ha il suo anello nel
 * bottone, e coprire quello che si sta leggendo sarebbe peggio.
 */
export function CaricoSezione({ attiva }: { attiva: boolean }) {
  if (!attiva) return null;
  return (
    <div className="md-carico">
      <div className="md-carico__velo">
        <Logo size={64} anim="costruzione" label={t('Carico…')} />
      </div>
    </div>
  );
}

/** Il logotipo da solo, per la barra e le intestazioni strette. */
export function Wordmark({ size = 28 }: { size?: number }) {
  return <span className="molo-wordmark" style={{ fontSize: size }}>Molo</span>;
}

/** Segno e logotipo insieme, incolonnati: è la forma della schermata d'accesso. */
export function Brandmark({ anim }: { anim?: Animazione } = {}) {
  return (
    <div className="molo-brand" role="img" aria-label="Molo">
      <Logo anim={anim} />
      <Wordmark />
    </div>
  );
}
